/* eslint-disable */
import React, { useEffect, useState } from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import PersonAddOutlinedIcon from '@material-ui/icons/PersonAddOutlined';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import Grid from '@material-ui/core/Grid';

import { useDispatch } from 'react-redux';
import { createClient, updateClient } from '../../actions/clientActions';
import { useLocation } from 'react-router-dom';

import { useSnackbar } from '../../utils/useSnackbar';

const styles = (theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1.25, 2),
    backgroundColor: '#ef7f1a',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  titleIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 26,
    height: 26,
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.18)',
    flexShrink: 0,
  },
  closeButton: {
    marginLeft: 'auto',
    color: '#fff',
    padding: 6,
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <span className={classes.titleIcon}>
        <PersonAddOutlinedIcon style={{ fontSize: 15 }} />
      </span>
      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{children}</Typography>
      {onClose ? (
        <IconButton aria-label="close" className={classes.closeButton} size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: theme.spacing(2.5, 2.5, 1.5),
  },
}))(MuiDialogContent);

const DialogActions = withStyles((theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1, 2, 1.5),
    gap: theme.spacing(1),
  },
}))(MuiDialogActions);

// react-phone-number-input ships unstyled and doesn't match MUI's outlined
// look on its own - these overrides key off its own class names (no MUI
// override point exists for a 3rd-party input) to bring it in line with the
// Name/Address fields next to it: same height/radius/border colors, a
// visible error state (the plain library has no error styling at all), and
// a focus ring in the app's own orange rather than the library's default blue.
const useFieldStyles = makeStyles((theme) => ({
  phoneField: {
    '& .PhoneInput': {
      display: 'flex',
      alignItems: 'center',
      border: '1px solid rgba(0,0,0,0.23)',
      borderRadius: 4,
      padding: '0 10px',
      height: 40,
      transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    },
    // `:hover` counts as one extra selector for specificity purposes, so
    // `.PhoneInput:hover` (3 selectors) would otherwise outrank
    // `.PhoneInput--focus` (2 selectors) and win the border-color whenever
    // the cursor is still sitting over the field right after focusing it -
    // exactly the common case. Excluding the focused state here keeps hover
    // and focus from fighting over the same property.
    '& .PhoneInput:hover:not(.PhoneInput--focus)': {
      borderColor: 'rgba(0,0,0,0.87)',
    },
    '& .PhoneInput--focus': {
      borderColor: '#ef7f1a',
      borderWidth: 2,
      padding: '0 9px',
      boxShadow: '0 0 0 3px rgba(239,127,26,0.12)',
    },
    '&.hasError .PhoneInput': {
      borderColor: theme.palette.error.main,
    },
    '&.hasError .PhoneInput--focus': {
      boxShadow: `0 0 0 3px ${theme.palette.error.main}22`,
    },
    '& .PhoneInputInput': {
      border: 'none',
      outline: 'none',
      fontSize: '0.9rem',
      fontFamily: theme.typography.fontFamily,
      padding: '8px 0',
      background: 'transparent',
    },
    '& .PhoneInputCountry': {
      marginRight: 8,
    },
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: 'rgba(0,0,0,0.6)',
    marginBottom: 4,
    display: 'block',
  },
  helperError: {
    display: 'block',
    marginTop: 4,
    marginLeft: 2,
    fontSize: '0.7rem',
    lineHeight: 1.4,
  },
}));

const AddClient = ({ setOpen, open, editingClient = null, onSaved }) => {
  const location = useLocation();
  const classes = useFieldStyles();
  const [clientData, setClientData] = useState({ name: '', email: '', phone: '', address: '', gstNo: '', dueDate: '', contactPerson: '', userId: [] });
  const dispatch = useDispatch();
  const user = JSON.parse(localStorage.getItem('profile'));
  const [openSnackbar, closeSnackbar] = useSnackbar();
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const nameInputRef = React.useRef(null);

  // Single effect owns the whole clientData object on every open/editingClient
  // change - was previously two separate effects (one deriving userId from
  // the logged-in user on `[location]`, one pre-filling from editingClient on
  // `[open, editingClient]`) that each did a partial `{...clientData, ...}`
  // spread from their own closure. Firing independently, in either order,
  // they could stomp on each other's write (e.g. the userId effect's stale
  // spread silently reverting a just-applied name/phone pre-fill back to
  // blank) - that's what was leaving Name/Phone empty after a quick-edit.
  // Building one complete object per branch here removes that race entirely.
  useEffect(() => {
    if (!open) return;
    const checkId = user?.result?._id;
    const derivedUserId = checkId !== undefined ? [checkId] : [user?.result?.googleId];
    if (editingClient) {
      setClientData({
        name: editingClient.name || '',
        email: editingClient.email || '',
        phone: editingClient.phone || '',
        address: editingClient.address || '',
        gstNo: editingClient.gstNo || '',
        dueDate: editingClient.dueDate || '',
        contactPerson: editingClient.contactPerson || '',
        userId: editingClient.userId || derivedUserId,
      });
    } else {
      setClientData({
        name: '',
        email: '',
        phone: '',
        address: '',
        gstNo: '',
        dueDate: '',
        contactPerson: '',
        userId: derivedUserId,
      });
    }
    setErrors({});
    setSubmitting(false);
    // Focus (and select any pre-filled text) once the dialog's own open
    // transition has settled, so it isn't stolen by the Dialog itself.
    const focusTimer = setTimeout(() => {
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 100);
    return () => clearTimeout(focusTimer);
    // eslint-disable-next-line
  }, [open, editingClient, location]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setClientData((prevData) => ({
      ...prevData,
      // react-phone-number-input's onChange fires with `value: undefined`
      // when the field is cleared/unparseable, rather than ''. Coercing
      // here keeps clientData fields always-strings so validateForm's
      // .trim() calls below can never crash on an undefined field.
      [name]: value ?? '',
    }));
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '', // Clear the error for the specific field as the user edits it
    }));
  };

  // Per-field check reused by both the live onBlur feedback below and the
  // full validateForm() gate on submit, so the two can never drift apart.
  const validateField = (name, value) => {
    if (name === 'name') {
      const trimmed = (value || '').trim();
      if (!trimmed) return 'Name is required';
      return '';
    }
    if (name === 'phone') {
      const trimmed = (value || '').trim();
      if (!trimmed) return 'Phone number is required';
      return '';
    }
    return '';
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const validateForm = () => {
    const newErrors = {
      name: validateField('name', clientData.name),
      phone: validateField('phone', clientData.phone),
      address: validateField('address', clientData.address),
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmitClient = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validateForm()) return;
    setSubmitting(true);
    try {
      const saved = editingClient
        ? await dispatch(updateClient(editingClient._id, clientData, openSnackbar))
        : await dispatch(createClient(clientData, openSnackbar));

      onSaved?.(saved);
      clear();
      handleClose();
    } finally {
      setSubmitting(false);
    }
  };

  const clear = () => {
    // Keep userId - it's re-derived from the logged-in user, not per-form
    // state, and blanking it here would silently drop the owner scoping on
    // whichever customer gets created/edited next in this same page visit.
    setClientData((prev) => ({ name: '', email: '', phone: '', address: '', gstNo: '', contactPerson: '', dueDate: '', userId: prev.userId }));
    setErrors({});
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
  };

  return (
    <div>
      <Dialog onClose={handleClose} aria-labelledby="customized-dialog-title" open={open} fullWidth maxWidth="xs">
        <DialogTitle id="customized-dialog-title" onClose={handleClose}>
          {editingClient ? `Edit Customer — ${editingClient.name || clientData.name}` : 'New Customer'}
        </DialogTitle>
        <form onSubmit={handleSubmitClient} noValidate>
          <DialogContent dividers>
            {editingClient && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  backgroundColor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  marginBottom: 16,
                  fontSize: '0.8125rem',
                }}
              >
                <div>
                  <span style={{ color: '#94a3b8', fontWeight: 500 }}>Customer: </span>
                  <strong style={{ color: '#1e293b' }}>{editingClient.name || clientData.name}</strong>
                </div>
                {(editingClient.phone || clientData.phone) && (
                  <span style={{ color: '#64748b', fontSize: '0.75rem' }}>
                    {editingClient.phone || clientData.phone}
                  </span>
                )}
              </div>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  size="small"
                  label="Customer Name"
                  required
                  fullWidth
                  name="name"
                  value={clientData.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.name}
                  helperText={errors.name}
                  inputRef={nameInputRef}
                  onFocus={(e) => e.target.select()}
                />
              </Grid>
              <Grid item xs={12}>
                <span className={classes.fieldLabel}>Phone Number *</span>
                <div className={`${classes.phoneField} ${errors.phone ? 'hasError' : ''}`}>
                  <PhoneInput
                    placeholder="Enter phone number"
                    value={clientData.phone}
                    onChange={(value) => handleChange({ target: { name: 'phone', value } })}
                    onBlur={() => handleBlur({ target: { name: 'phone', value: clientData.phone } })}
                    defaultCountry="TH"
                    international
                    countryCallingCodeEditable={false}
                    inputProps={{ name: 'phone', required: true }}
                  />
                </div>
                {!!errors.phone && (
                  <Typography className={classes.helperError} color="error">
                    {errors.phone}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  size="small"
                  label="Address"
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={3}
                  name="address"
                  value={clientData.address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!errors.address}
                  helperText={errors.address}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={submitting} color="default" size="small">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
              style={{ backgroundColor: '#ef7f1a', color: 'white' }}
            >
              {submitting ? 'Saving…' : editingClient ? 'Save Changes' : 'Save Customer'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  );
};

export default AddClient;
